import { useEffect, useState } from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { App } from "@back4app2/sdk";
import { DsButton, ScreenHead, SurfaceCard } from "../components/ds";
import type { SheetRow } from "../components/ds";
import { AlertIcon, UploadIcon } from "../components/ds/icons";
import CodeViewer from "../components/UploadSource/CodeViewer";
import UploadNewCodeDialog from "../components/UploadSource/UploadNewCodeDialog";
import {
  ApiV2Error,
  AppSource,
  archiveDeployment,
  archiveSource,
  DeploymentActions,
  getAppSource,
  getDeploymentActions,
} from "../utils/apiV2";
import { downloadArchive } from "../utils/archiveDownload";
import { formatDateTime } from "../utils/dates";
import { appSourceKind } from "../utils/appSource";
import { canReadSource } from "../utils/permissions";
import { canRunAppActions } from "../components/MyAppActions";

/**
 * Source Code: the app's uploaded code, read-only, with the rest of the
 * window to read it in. It used to be a "View files" dialog over Settings,
 * which capped the editor at two thirds of the screen.
 *
 * Shows the app's latest code — the one that is edited — or, opened from the
 * Deployments list's code button (`?deployment=`), one deployment's while it
 * is kept: read-only, under a line that says which deployment, when it was
 * made and how it ended, with the way back to the latest. There is no picker
 * of versions here: the Deployments list is where the versions are, with
 * their dates and their states.
 *
 * Only for apps that deploy from uploads: a GitHub app's code is on GitHub,
 * and an image app has none of its own here.
 */

const MyAppSource = () => {
  const { app, setScreenActions } = useOutletContext<{
    app: App;
    /** The phone header's "⋯": this screen's rows go first in it. */
    setScreenActions: (rows: SheetRow[]) => void;
  }>();
  const env = app?.mainService?.mainServiceEnvironment;
  const isUpload =
    appSourceKind(env, !!app?.mainService?.repository) === "UPLOAD";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deploymentId = searchParams.get("deployment") || undefined;

  const [source, setSource] = useState<AppSource | null>(null);
  const [actions, setActions] = useState<Record<
    string,
    DeploymentActions
  > | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  // Uploading code is a deploy: the same gate as the Action menu's Deploy and
  // the rollback, and the one the API applies (deploy access, app not blocked
  // or awaiting payment). Settings › Build & deploy asks the same.
  const canUpload = !!app && canRunAppActions(app);
  const canRead = !!app && canReadSource(app);

  // The support launcher floats over the corner the editor's save bar uses;
  // this screen goes without it (index.css).
  useEffect(() => {
    document.body.classList.add("b4a2-source-screen");
    return () => document.body.classList.remove("b4a2-source-screen");
  }, []);

  // Bumped when the code changed from here (the editor saved, or updated
  // under its edits), so the latest is read again.
  const [codeVersion, setCodeVersion] = useState(0);

  // What the viewer offers on the phone (copy, download, export, the open
  // file's edits): rows of the header's "⋯", with Upload first.
  const [viewerRows, setViewerRows] = useState<SheetRow[]>([]);
  useEffect(() => {
    setScreenActions([
      ...(canUpload
        ? [{ label: "Upload files", onClick: () => setIsUploadOpen(true) }]
        : []),
      ...viewerRows,
    ]);
    return () => setScreenActions([]);
  }, [setScreenActions, canUpload, viewerRows]);

  useEffect(() => {
    if (!app || !isUpload || !canRead) return;
    let cancelled = false;
    setLoadError(null);
    getAppSource(app.id)
      .then((result) => !cancelled && setSource(result))
      .catch(
        (e) =>
          !cancelled &&
          setLoadError(
            e instanceof ApiV2Error
              ? e.message
              : "This app's code could not be loaded."
          )
      );
    // Only for the line over an earlier deployment's code (its number, date
    // and state); without it the screen still shows the code.
    getDeploymentActions(app.id)
      .then((result) => !cancelled && setActions(result))
      .catch((e) => console.error("could not load deployment versions", e));
    return () => {
      cancelled = true;
    };
  }, [app, isUpload, canRead, codeVersion]);

  const latest = source?.latest;
  const picked = deploymentId && actions ? actions[deploymentId] : undefined;

  // The whole code as a .zip, built on demand: asks until it is ready.
  const downloadZip = async () => {
    setIsArchiving(true);
    await downloadArchive(() =>
      deploymentId
        ? archiveDeployment(app.id, deploymentId)
        : archiveSource(app.id)
    );
    setIsArchiving(false);
  };

  if (!isUpload || !canRead) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHead title="Source Code" />
        <SurfaceCard
          tone="surface"
          className="px-5 py-4 text-ds-125 text-navy-200"
        >
          {!isUpload
            ? "This app doesn't deploy from uploaded files, so there is no code to show here."
            : "Your access to this app doesn't include its code. Ask the app owner for Build & Deploy access."}
        </SurfaceCard>
      </div>
    );
  }

  const hasCode = !!latest || !!deploymentId;

  // New code is a deploy: follow it to where the build shows, as Settings does.
  const uploadDialog = canUpload ? (
    <UploadNewCodeDialog
      appId={app.id}
      open={isUploadOpen}
      onClose={() => setIsUploadOpen(false)}
      onDeployed={() => navigate(`/apps/${app.id}`)}
    />
  ) : null;

  // Over an earlier deployment's code: which one, when, how it ended, and
  // the way back. The same line on the phone and the desktop.
  const deploymentBanner = deploymentId ? (
    // Edge to edge on the desktop, as the viewer under it is: boxed and
    // inset, it read as a card of the page instead of a line of the viewer.
    <div className="flex-none flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 rounded-card border border-navy-500 bg-navy-800 px-4 py-2.5 text-ds-125 text-navy-100 lg:mb-0 lg:-mx-8 lg:rounded-none lg:border-x-0 lg:border-b-0 lg:px-8">
      <span className="font-courrier text-white">
        {picked ? `#${picked.number} · ${picked.codeHash}` : "Deployment"}
      </span>
      {picked ? (
        <span className="text-navy-200">
          deployed {formatDateTime(new Date(picked.createdAt))}
        </span>
      ) : null}
      <span className="text-navy-300">
        {picked?.rollback === "CURRENT"
          ? "The code running now, read-only here."
          : "The code of an earlier deployment, read-only."}
      </span>
      <button
        type="button"
        onClick={() => setSearchParams({})}
        className="b4-focus ml-auto rounded-control font-semibold text-action-soft hover:text-white"
      >
        Back to latest →
      </button>
    </div>
  ) : null;

  return (
    // Desktop: the whole height of the window, the editor gets everything
    // below the head, and it takes back all of main's bottom padding (kept
    // for the phone's tab bar), running to the bottom of the window.
    // Phone: the page scrolls, and the viewer has a height of its own — made
    // to fit the window there, the head and the tree left the editor a sliver.
    <div className="flex flex-col lg:flex-1 lg:min-h-0 lg:-mb-24">
      <div className="flex-none flex flex-wrap items-center justify-between gap-3 mb-4 [@media(max-width:767.98px)]:hidden">
        <ScreenHead title="Source Code" className="!mb-0">
          {/* Named, not hashed: a seven-character id says nothing to the
              person reading it. The hash is in the Deployments list. */}
          {hasCode && !deploymentId ? (
            <span className="rounded-control border border-navy-400 bg-navy-800 px-2.5 py-1 font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-200">
              latest
            </span>
          ) : null}
        </ScreenHead>
        {/* Everything you can do with the code — copy, download a file,
            export it all, send new code — is on the viewer's file bar, as the
            Agents code panel has it; the head keeps only the name and the
            version. Deployments are in the sidebar. Without code there is no
            viewer, so the one action is here. */}
        {canUpload && !hasCode ? (
          <DsButton
            variant="action"
            size="lg"
            className="flex-none"
            onClick={() => setIsUploadOpen(true)}
          >
            <UploadIcon size={16} /> Upload new code
          </DsButton>
        ) : null}
      </div>

      {deploymentBanner}

      {loadError ? (
        <div
          role="alert"
          className="flex items-center gap-2 text-ds-125 text-state-err"
        >
          <AlertIcon size={14} /> {loadError}
        </div>
      ) : !source ? (
        <div className="font-courrier text-ds-115 text-navy-200">
          Loading code…
        </div>
      ) : !hasCode ? (
        <SurfaceCard
          tone="surface"
          className="px-5 py-4 text-ds-125 text-navy-200"
        >
          No code uploaded yet.
          {canUpload ? " Upload a folder to deploy it." : ""}
        </SurfaceCard>
      ) : (
        // Desktop: edge to edge — against the sidebar, the window's right side
        // and its bottom — so the viewer's rules run into edges instead of
        // stopping short of them in the middle of the page.
        <div className="h-[80vh] lg:h-auto lg:flex-1 lg:min-h-[28rem] lg:-mx-8">
          <CodeViewer
            appId={app.id}
            deploymentId={deploymentId}
            reloadKey={latest?.uploadId}
            onExport={downloadZip}
            onUpload={canUpload ? () => setIsUploadOpen(true) : undefined}
            // Editing is a deploy too: the same gate as uploading.
            canEdit={canUpload}
            onCodeChanged={() => setCodeVersion((version) => version + 1)}
            isExporting={isArchiving}
            onPhoneRows={setViewerRows}
          />
        </div>
      )}
      {uploadDialog}
    </div>
  );
};

export default MyAppSource;
